import { ChangeDetectorRef, Component, ElementRef, inject, input, output, viewChildren } from '@angular/core';
import {
    FormArray,
    FormControl,
    FormGroup,
    NonNullableFormBuilder,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgxMaskDirective } from 'ngx-mask';
import { EMPTY } from 'rxjs';
import { catchError, finalize, timeout } from 'rxjs/operators';

import { IconComponent } from '../../../shared/components/icons/icon/icon.component';
import { ModalComponent } from '../../../shared/components/modal/modal/modal.component';
import { TooltipComponent } from '../../../shared/components/tooltip/tooltip.component';
import { useToaster } from '../../../shared/composables/use-toaster';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { GatewayId } from '../../../site-gateway/models/gateway';
import { LinkRequestsService } from '../../store/api/link-requests.service';

const linkRequestErrorToMessage: Record<string, string> = {
    INSTALLER_ALREADY_LINKED_TO_SITE: 'MODAL_ATTACH_GATEWAY_ALREADY_LINKED',
    PENDING_LINK_REQUEST_ALREADY_EXISTS: 'MODAL_ATTACH_GATEWAY_ALREADY_EXISTS',
    WRONG_OWNER_EMAIL: 'MODAL_ATTACH_GATEWAY_WRONG_ASSOCIATION',
    SITE_NOT_FOUND_FOR_GATEWAY: 'MODAL_ATTACH_GATEWAY_WRONG_ASSOCIATION',
};

const PIN_SEGMENT_COUNT = 3;
const PIN_SEGMENT_LENGTH = 4;

interface CreateLinkRequestForm {
    gatewayId: FormArray<FormControl<string>>;
    siteOwnerEmail: FormControl<string>;
}

@Component({
    selector: 'app-link-request-modal',
    templateUrl: './create-link-request-modal.component.html',
    styleUrls: ['./create-link-request-modal.component.scss'],
    imports: [IconComponent, ModalComponent, ReactiveFormsModule, NgxMaskDirective, TooltipComponent, TranslatePipe],
})
export class CreateLinkRequestModalComponent {
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly linkRequestsService = inject(LinkRequestsService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();
    private readonly analyticsService = inject(AnalyticsService);

    readonly open = input<boolean>(false);
    readonly closed = output<void>();
    readonly pinSegmentCount = PIN_SEGMENT_COUNT;

    formGroup: FormGroup<CreateLinkRequestForm>;
    loading = false;
    showConfirmFieldHints = false;
    attachRequestErrorCode?: string;

    private readonly pinInputs = viewChildren<ElementRef<HTMLInputElement>>('pinInput');

    constructor() {
        this.attachRequestErrorCode = undefined;

        this.formGroup = new FormGroup<CreateLinkRequestForm>({
            gatewayId: this.formBuilder.array(
                Array.from({ length: PIN_SEGMENT_COUNT }, () =>
                    this.formBuilder.control('', [Validators.required, Validators.pattern(/^\d{4}$/)]),
                ),
            ),
            siteOwnerEmail: this.formBuilder.control('', [Validators.required, Validators.email]),
        });
    }

    get pinControls(): FormControl<string>[] {
        return this.formGroup.controls.gatewayId.controls;
    }

    get confirmHint(): string {
        return this.pinValidationTooltip ?? this.emailValidationTooltip ?? '';
    }

    get pinValidationTooltip(): string | null {
        if (this.loading || this.formGroup.controls.gatewayId.valid) {
            return null;
        }

        return this.pinControls.every((control) => control.value.length === 0)
            ? this.translate.instant('FORM_VALIDATION_ERROR_REQUIRED')
            : this.translate.instant('MODAL_ATTACH_GATEWAY_TEXT1');
    }

    get emailValidationTooltip(): string | null {
        if (this.loading || this.formGroup.controls.siteOwnerEmail.valid) {
            return null;
        }

        return this.formGroup.controls.siteOwnerEmail.hasError('required')
            ? this.translate.instant('FORM_VALIDATION_ERROR_REQUIRED')
            : this.translate.instant('FORM_VALIDATION_ERROR_INVALID_EMAIL');
    }

    onConfirmHintVisible(visible: boolean): void {
        this.showConfirmFieldHints = visible;
    }

    submit(): void {
        if (this.formGroup.invalid || this.loading) {
            return;
        }

        const { gatewayId: gatewayIdSegments, siteOwnerEmail } = this.formGroup.getRawValue();
        const gatewayId = gatewayIdSegments.join('-');

        this.loading = true;
        this.attachRequestErrorCode = undefined;
        this.analyticsService.boxLinkingRequested();
        this.createLinkRequest(gatewayId, siteOwnerEmail);
    }

    onPinInput(segmentIndex: number): void {
        this.clearRequestError();

        const input = this.pinInputs()[segmentIndex]?.nativeElement;
        if (input?.value.length === PIN_SEGMENT_LENGTH) {
            this.pinInputs()[segmentIndex + 1]?.nativeElement.focus();
        }
    }

    onPinKeydown(event: KeyboardEvent, segmentIndex: number): void {
        const input = event.currentTarget as HTMLInputElement;
        if (event.key === 'Backspace' && input.value.length === 0) {
            this.pinInputs()[segmentIndex - 1]?.nativeElement.focus();
        }
    }

    onPinPaste(event: ClipboardEvent): void {
        const digits = event.clipboardData?.getData('text').replace(/\D/g, '') ?? '';
        if (digits.length !== PIN_SEGMENT_COUNT * PIN_SEGMENT_LENGTH) {
            return;
        }

        event.preventDefault();
        this.pinControls.forEach((control, index) => {
            const start = index * PIN_SEGMENT_LENGTH;
            control.setValue(digits.slice(start, start + PIN_SEGMENT_LENGTH));
        });
        this.pinInputs()[PIN_SEGMENT_COUNT - 1]?.nativeElement.focus();
        this.clearRequestError();
    }

    clearRequestError(): void {
        this.attachRequestErrorCode = undefined;
    }

    cancel(): void {
        if (this.loading) {
            return;
        }

        this.close();
    }

    generateLocalizedErrorKey(): string {
        return (
            (this.attachRequestErrorCode && linkRequestErrorToMessage[this.attachRequestErrorCode]) ||
            'MODAL_ATTACH_GATEWAY_UNKNOWN_ERROR'
        );
    }

    private createLinkRequest(gatewayId: GatewayId, siteOwnerEmail: string) {
        return this.linkRequestsService
            .createLinkRequest({ siteOwnerEmail, gatewayId })
            .pipe(
                timeout(10 * 1000),
                catchError((err) => {
                    this.attachRequestErrorCode = err?.error?.errorCode;
                    if (!this.attachRequestErrorCode) {
                        this.toaster.error({
                            title: this.translate.instant('ERROR_WHILE_SENDING_SITE_ASSIGNATION_REQUEST'),
                        });
                    }

                    return EMPTY;
                }),
                finalize(() => {
                    this.loading = false;
                    this.changeDetectorRef.detectChanges();
                }),
            )
            .subscribe(() =>
                this.successCallback(
                    'CMS_SITE_ASSIGNATION_TOASTER_SUCCESS_TITLE',
                    'CMS_SITE_ASSIGNATION_TOASTER_SUCCESS_MESSAGE',
                ),
            );
    }

    private successCallback(toasterTitle: string, toasterMessage: string): void {
        this.toaster.success({
            title: this.translate.instant(toasterTitle),
            message: this.translate.instant(toasterMessage),
        });
        this.close();
    }

    private close(): void {
        this.formGroup.reset();
        this.attachRequestErrorCode = undefined;
        this.closed.emit();
        this.changeDetectorRef.detectChanges();
    }
}
